<script setup lang="ts">
import { usdcToken } from '@shared/data/token'
import { WalletConnectStatus } from '@shared/types'
import { Wallet } from '@injectivelabs/wallet-base/light'
import { Status, StatusType } from '@injectivelabs/utils/status'
import { localStorage } from '@/app/services/localStorage'
import { setPolymarketConnectedAccountAddress } from '@/app/services/polymarket/connected-account'
import { InitialStatusKey } from '@/types/symbols'

const route = useRoute()
const router = useRouter()
const walletStore = useWalletStore()
const sharedGeoStore = useSharedGeoStore()
const sharedTokenStore = useSharedTokenStore()
const sharedWalletStore = useSharedWalletStore()
const sharedDerivativeStore = useSharedDerivativeStore()
const { $onError } = useNuxtApp()

const status = reactive(new Status(StatusType.Loading))

let walletAddressTrackingTimeout: undefined | ReturnType<typeof setTimeout>

onMounted(() => {
  handleGoogleOAuth()
  sharedTokenStore.fetchTokensUsdPriceMapLean()
  sharedTokenStore.setSupplyMap({ [usdcToken.denom]: '0' })

  const initWallets = walletStore.init().then(() => {
    setPolymarketConnectedAccountAddress(sharedWalletStore.address)
  })

  Promise.all([
    initWallets,
    sharedGeoStore.fetchGeoLocation(),
    sharedDerivativeStore.fetchAllMarkets()
  ])
    .catch($onError)
    .then(() => {
      if (sharedWalletStore.isUserConnected) {
        scheduleWalletAddressTracking(sharedWalletStore.injectiveAddress)
      }
    })
    .finally(() => {
      status.setIdle()
    })
})

onUnmounted(() => {
  if (walletAddressTrackingTimeout) {
    clearTimeout(walletAddressTrackingTimeout)
  }
})

function scheduleWalletAddressTracking(injectiveAddress: string) {
  walletAddressTrackingTimeout = setTimeout(() => {
    if (
      !sharedWalletStore.isUserConnected ||
      sharedWalletStore.injectiveAddress !== injectiveAddress
    ) {
      return
    }

    import('@/app/providers/mixpanel/WalletTracker')
      .then(({ trackWalletAddress }) => {
        trackWalletAddress(injectiveAddress)
      })
      .catch($onError)
  }, 3000)
}

function handleGoogleOAuth() {
  if (sharedWalletStore.isUserConnected) {
    return
  }

  if (!route.hash) {
    return
  }

  const params = new URLSearchParams(route.hash.substring(1))
  const idToken = params.get('id_token')

  if (!idToken) {
    return
  }

  router.replace({ hash: '' })

  sharedWalletStore
    .initTurnkeyGoogle(idToken)
    .then(() => {
      const redirectPath = localStorage.get('googleOAuthRedirect', '') as string
      localStorage.remove('googleOAuthRedirect')

      if (redirectPath) {
        router.replace(redirectPath)
      }

      import('@/app/providers/mixpanel/WalletTracker')
        .then(({ trackLogin }) =>
          trackLogin({
            wallet: Wallet.Turnkey,
            evmAddress: sharedWalletStore.address,
            address: sharedWalletStore.injectiveAddress
          })
        )
        .catch($onError)
    })
    .catch($onError)
    .finally(() => {
      sharedWalletStore.walletConnectStatus = WalletConnectStatus.idle
    })
}

provide(InitialStatusKey, status)

useIntervalFn(() => sharedTokenStore.fetchTokensUsdPriceMapLean(), 30 * 1000)

watch(
  () => sharedWalletStore.address,
  (newAddress) => {
    setPolymarketConnectedAccountAddress(newAddress)
  }
)
</script>

<template>
  <UApp
    v-bind="{
      toaster: {
        position: 'bottom-right',
        class: 'z-1110 w-[calc(100%-2rem)] xs:w-md'
      }
    }"
  >
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </UApp>
</template>
